import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TLManagerService } from '../../services/tlManagerService';
import { BarChart3, DollarSign, BookOpen, CheckCircle, XCircle, Search, Calendar } from 'lucide-react';

const TLManagerPerformance = () => {
  const { data: performanceDataResult, isLoading } = useQuery({
    queryKey: ['tlManagerPerformance'],
    queryFn: () => TLManagerService.getPerformanceData(),
  });
  const performanceData = performanceDataResult?.data || {};

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Performance</h1>
        <p className="text-gray-600 mt-1">Track team lead performance metrics</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Total Revenue</p>
            <DollarSign size={20} className="text-green-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">
            ₹{Number(performanceData.totalRevenue || 0).toLocaleString('en-IN')}
          </p>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Booked Orders</p>
            <BookOpen size={20} className="text-blue-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{performanceData.bookedOrders || 0}</p>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Team Leads</p>
            <BarChart3 size={20} className="text-rose-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{performanceData.teamLeads || 0}</p>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Conversion Rate</p>
            <BarChart3 size={20} className="text-purple-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{performanceData.conversionRate || 0}%</p>
        </div>
      </div>
    </div>
  );
};

const TLManagerPaymentVerification = () => {
  const { data: paymentsData, isLoading, refetch } = useQuery({
    queryKey: ['tlManagerPayments'],
    queryFn: () => TLManagerService.getPaymentVerifications(),
  });
  const payments = paymentsData?.data || [];

  const handleVerify = async (paymentId, verified) => {
    const result = await TLManagerService.verifyPayment(paymentId, verified);
    if (result.success) {
      alert(`Payment ${verified ? 'verified' : 'rejected'} successfully!`);
      refetch();
    } else {
      alert('Failed to update payment: ' + result.error);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Payment Verification</h1>
        <p className="text-gray-600 mt-1">Verify payment proofs</p>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
          <div className="w-16 h-16 border-4 border-rose-500 border-t-transparent rounded-full spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading payments...</p>
        </div>
      ) : payments.length === 0 ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
          <DollarSign size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500">No payments pending verification</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Team Lead</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {payments.map((payment) => (
                <tr key={payment.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">#{payment.id?.slice(0, 8)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">#{payment.order_id?.slice(0, 8) || 'N/A'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">₹{Number(payment.amount || 0).toLocaleString('en-IN')}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">{payment.team_lead_name || 'N/A'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">{payment.created_at ? new Date(payment.created_at).toLocaleDateString() : 'N/A'}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded ${
                      payment.verified ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {payment.verified ? 'Verified' : 'Pending'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {!payment.verified && (
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleVerify(payment.id, true)}
                          className="px-3 py-1 bg-green-500 text-white rounded-lg hover:bg-green-600 text-sm"
                        >
                          <CheckCircle size={16} />
                        </button>
                        <button
                          onClick={() => handleVerify(payment.id, false)}
                          className="px-3 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm"
                        >
                          <XCircle size={16} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const TLManagerBooked = () => {
  const { data: bookedOrdersData, isLoading } = useQuery({
    queryKey: ['tlManagerBookedOrders'],
    queryFn: () => TLManagerService.getBookedOrders(),
  });
  const bookedOrders = bookedOrdersData?.data || [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Booked Orders</h1>
        <p className="text-gray-600 mt-1">View all booked orders</p>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
          <div className="w-16 h-16 border-4 border-rose-500 border-t-transparent rounded-full spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading orders...</p>
        </div>
      ) : bookedOrders.length === 0 ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
          <BookOpen size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500">No booked orders found</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Team Lead</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Telecaller</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {bookedOrders.map((order) => (
                <tr key={order.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">#{order.id?.slice(0, 8)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">{order.customer_name || 'N/A'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">{order.team_lead_name || 'N/A'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">{order.telecaller_name || 'N/A'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">₹{Number(order.amount || 0).toLocaleString('en-IN')}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">{order.created_at ? new Date(order.created_at).toLocaleDateString() : 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export { TLManagerPerformance, TLManagerPaymentVerification, TLManagerBooked };